import { Component, Property } from '@wonderlandengine/api';
import { vec3 } from 'gl-matrix';

/* ─────────────────────────────────────────────────────────────────────────────
 * ZombieBot (Simplified Swarm)
 *
 * • Relentless Chase: Never roams, always tracks the player.
 * • Streaming Aware: Spawns at the outer rim of loaded chunks.
 * • Voxel Snapping: Queries VoxelTreadmill directly to climb blocks and terrain.
 * • Zero-Dependency: No Recast/NavMesh overhead.
 * ───────────────────────────────────────────────────────────────────────────── */

export class ZombieBot extends Component {
    static TypeName = 'zombie-bot';

    static Properties = {
        player:          Property.object(),
        maxHealth:       Property.int(75),
        moveSpeed:       Property.float(2),
        attackRange:     Property.float(1),
        attackDamage:    Property.int(10),
        attackCooldown:  Property.float(1.0),
        respawnDelay:    Property.float(3.0),
    };

    start() {
        /* Ensure the class is globally accessible for ID synchronization */
        window.ZombieBotClass = ZombieBot;
        this.zombieId = (ZombieBot._nextId = (ZombieBot._nextId || 0) + 1);

        this._pos       = vec3.create();
        this._playerPos = vec3.create();
        this._tmOffset  = vec3.create();
        this._up        = vec3.fromValues(0, 1, 0);
        // Hide position in LOCAL (grid) space — Y -1000 is far below any terrain.
        this._hidePos   = vec3.fromValues(0, -1000, 0);

        // Runtime clones managed by GameLoop: preserve the state GameLoop already set
        // (active/dead/player) and skip self-registration — GameLoop pushed us already.
        if (this.object._dvClone) {
            this._isRemote = false;
            return;
        }

        this._active      = false;
        this._dead        = true;
        this._health      = this.maxHealth;
        this._state       = 'CHASING';
        this._attackTimer = 0;
        this._targetPeerId = null;
        this._isJumping   = false;
        this._jumpVelY    = 0;

        // Auto-activate after world init — only when game has started.
        if (this.player) {
            const tryActivate = () => {
                if (window.GameState === 'playing') {
                    this.activate();
                } else {
                    setTimeout(tryActivate, 500);
                }
            };
            setTimeout(tryActivate, 2000);
        }

        (window.ZombieBots = window.ZombieBots || []).push(this);
        this._isRemote = false;
    }

    update(dt) {
        if (window.GameState !== 'playing') return;
        if (!this._active || this._dead || !this.player) return;
        if (this._isRemote) return;

        const vt = window.VoxelTreadmill;
        if (!vt) return;

        // ── All positions in TREADMILL LOCAL space = grid coords ──────────────
        this.object.getPositionLocal(this._pos);           // zombie grid pos
        vt.object.getPositionWorld(this._tmOffset);        // treadmill world pos

        // Player is near world origin; convert to grid: gridPos = worldPos - tmOffset
        this.player.getPositionWorld(this._playerPos);
        this._playerPos[0] -= this._tmOffset[0];
        this._playerPos[1] -= this._tmOffset[1];
        this._playerPos[2] -= this._tmOffset[2];


        // Remote players: their _remotePositions are world coords — convert to grid
        this._targetPeerId = null;
        let bestDistSq = (this._playerPos[0] - this._pos[0]) ** 2 +
                         (this._playerPos[2] - this._pos[2]) ** 2;
        const remotePos = window.OasisMultiplayer?._remotePositions;
        if (remotePos) {
            for (const [peerId, wpos] of remotePos) {
                /* Convert remote world pos to grid space for distance check and movement */
                const rx = wpos[0] - this._tmOffset[0];
                const ry = wpos[1] - this._tmOffset[1];
                const rz = wpos[2] - this._tmOffset[2];
                const dsq = (rx - this._pos[0]) ** 2 + (rz - this._pos[2]) ** 2;
                if (dsq < bestDistSq) {
                    bestDistSq = dsq;
                    this._targetPeerId = peerId;
                    this._playerPos[0] = rx;
                    this._playerPos[1] = ry;
                    this._playerPos[2] = rz;
                }
            }
        }

        if (this._attackTimer > 0) this._attackTimer -= dt;

        const dx   = this._playerPos[0] - this._pos[0];
        const dz   = this._playerPos[2] - this._pos[2];
        const dist = Math.sqrt(dx * dx + dz * dz);

        if (dist <= this.attackRange) {
            this._state = 'ATTACKING';
            this._doAttack(vt);
        } else {
            this._state = 'CHASING';
            this._doChase(dt, dx, dz, dist, vt);
        }
    }

    /* ── core logic ─────────────────────────────────────────────────────────── */

    _doChase(dt, dx, dz, dist, vt) {
        const STEP_UP_MAX = 1.1;
        const JUMP_VEL    = 7.0;
        const GRAVITY     = 20.0;

        const dirX = dx / dist;
        const dirZ = dz / dist;
        const stepDist = Math.min(this.moveSpeed * dt, Math.max(0, dist - this.attackRange * 0.85));

        // In local space, Y IS grid Y — no tmOffset subtraction needed.
        const currentGy = this._pos[1];
        const floorMin  = Math.floor(vt.floorLevel ?? -20);

        // lx/lz are already grid coords (local space = grid space).
        const getSurface = (lx, lz) => {
            const top = Math.floor(currentGy) + 2;
            for (let y = top; y >= floorMin; y--) {
                if (vt._isSolid(lx, y, lz)) return y + 1.0;
            }
            return currentGy;
        };

        const isBodyBlocked = (lx, lz, destSurf) => {
            const standY = Math.floor(destSurf);
            return vt._isSolid(lx, standY + 1, lz) || vt._isSolid(lx, standY + 2, lz);
        };

        const facePlayer = () => {
            if (dist > 0.1) {
                const yaw = Math.atan2(dx, dz) + Math.PI;
                const halfYaw = yaw * 0.5;
                this.object.setRotationWorld([0, Math.sin(halfYaw), 0, Math.cos(halfYaw)]);
            }
        };

        // ── Jump arc ──────────────────────────────────────────────────────────
        if (this._isJumping) {
            this._jumpVelY -= GRAVITY * dt;

            const newX = this._pos[0] + dirX * stepDist;
            const newZ = this._pos[2] + dirZ * stepDist;
            const newY = this._pos[1] + this._jumpVelY * dt;

            const landSurf = getSurface(newX, newZ);

            if (newY <= landSurf && this._jumpVelY < 0) {
                this._isJumping = false;
                this._jumpVelY  = 0;
                this.object.setPositionLocal([newX, landSurf, newZ]);
            } else {
                this.object.setPositionLocal([newX, newY, newZ]);
            }

            facePlayer();
            return;
        }

        // ── Normal grounded navigation ────────────────────────────────────────
        const directX    = this._pos[0] + dirX * stepDist;
        const directZ    = this._pos[2] + dirZ * stepDist;
        const directSurf = getSurface(directX, directZ);
        const directDelta = directSurf - currentGy;
        const directBlocked = isBodyBlocked(directX, directZ, directSurf) ||
                              directDelta > STEP_UP_MAX || directDelta < -STEP_UP_MAX;

        let chosenX = directX, chosenZ = directZ, chosenSurf = directSurf;

        if (directBlocked) {
            /* If blocked by a cliff drop, skip sidestepping and leap immediately */
            if (directDelta < -STEP_UP_MAX) {
                this._isJumping = true;
                this._jumpVelY  = JUMP_VEL;
                chosenX = this._pos[0]; chosenZ = this._pos[2]; chosenSurf = currentGy;
            } else {
            const px1 =  dirZ, pz1 = -dirX;
            const px2 = -dirZ, pz2 =  dirX;
            const tx1 = this._pos[0] + px1 * stepDist, tz1 = this._pos[2] + pz1 * stepDist;
            const tx2 = this._pos[0] + px2 * stepDist, tz2 = this._pos[2] + pz2 * stepDist;
            const s1  = getSurface(tx1, tz1), s2 = getSurface(tx2, tz2);
            const ok1 = !isBodyBlocked(tx1, tz1, s1) && Math.abs(s1 - currentGy) <= STEP_UP_MAX;
            const ok2 = !isBodyBlocked(tx2, tz2, s2) && Math.abs(s2 - currentGy) <= STEP_UP_MAX;

            if (ok1 && ok2) {
                const d1sq = (tx1 - this._playerPos[0]) ** 2 + (tz1 - this._playerPos[2]) ** 2;
                const d2sq = (tx2 - this._playerPos[0]) ** 2 + (tz2 - this._playerPos[2]) ** 2;
                if (d1sq <= d2sq) { chosenX = tx1; chosenZ = tz1; chosenSurf = s1; }
                else              { chosenX = tx2; chosenZ = tz2; chosenSurf = s2; }
            } else if (ok1) { chosenX = tx1; chosenZ = tz1; chosenSurf = s1; }
            else if (ok2)   { chosenX = tx2; chosenZ = tz2; chosenSurf = s2; }
            else {
                this._isJumping = true;
                this._jumpVelY  = JUMP_VEL;
                chosenX = this._pos[0]; chosenZ = this._pos[2]; chosenSurf = currentGy;
            }
            }
        }

        this.object.setPositionLocal([chosenX, chosenSurf, chosenZ]);
        facePlayer();
    }

    _doAttack(vt) {
        if (this._attackTimer > 0) return;
        this._attackTimer = this.attackCooldown;

        if (this._targetPeerId) {
            window.OasisMultiplayer?._broadcast?.({
                type: 'player_hit',
                targetPeerId: this._targetPeerId,
                damage: this.attackDamage,
            });
        } else {
            if (typeof vt._health === 'number') {
                let dmg = this.attackDamage;
                if (typeof vt._shield === 'number' && vt._shield > 0) {
                    const absorbed = Math.min(vt._shield, dmg);
                    vt._shield = Math.max(0, vt._shield - absorbed);
                    dmg -= absorbed;
                }
                vt._health = Math.max(0, vt._health - dmg);
            }
            if (typeof vt._applyHitAtWorld === 'function') {
                // Convert player grid pos back to world for hit effect
                // this._playerPos is already in world coordinates
                vt._applyHitAtWorld({
                    x: this._playerPos[0],
                    y: this._playerPos[1] - 0.5, // Adjust for player's feet
                    z: this._playerPos[2],
                }, 2, 'zombie');
            }
            window.GameInterface?.updateStats?.(vt._health, vt._shield || 0);
        }
    }

    /* ── lifecycle / combat ─────────────────────────────────────────────────── */

    takeDamage(amount) {
        if (this._dead) return;
        this._health -= amount;
        if (this._health <= 0) this._die();
    }

    _die() {
        this._dead   = true;
        this._active = false;
        this.object.setPositionLocal(this._hidePos);

        window.GameInterface?.pushConsoleLog?.('ZOMBIE ELIMINATED');

        const deathToken = window.DeadVoxRunToken || 0;
        const tryRespawn2 = () => {
            if ((window.DeadVoxRunToken || 0) !== deathToken) return;
            if (this._waveFreeze) { setTimeout(tryRespawn2, 500); return; }
            if (window.GameState !== 'playing') { setTimeout(tryRespawn2, 500); return; }
            if (window.DeadVoxLoop?._phase !== 'survive') { setTimeout(tryRespawn2, 500); return; }
            // Don't exceed the wave's concurrent spawn cap
            const cap = window.DeadVoxLoop?._waveSpawnCount ?? Infinity;
            const active = (window.ZombieBots || []).filter(z => z._active && !z._dead).length;
            if (active >= cap) { setTimeout(tryRespawn2, 1000); return; }
            this.activate();
        };
        setTimeout(tryRespawn2, this.respawnDelay * 1000);
    }

    activate() {
        const vt = window.VoxelTreadmill;
        if (!vt || !this.player) return;

        // Ensure parented to treadmill OBJECT so local space == grid space.
        if (vt.object && this.object.parent !== vt.object) {
            this.object.parent = vt.object;
        }

        this.active = true;

        if (!this._pos)       this._pos       = vec3.create();
        if (!this._playerPos) this._playerPos = vec3.create();
        if (!this._tmOffset)  this._tmOffset  = vec3.create();
        if (!this._hidePos)   this._hidePos   = vec3.fromValues(0, -1000, 0);
        if (!this._up)        this._up        = vec3.fromValues(0, 1, 0);

        this._health      = this.maxHealth;
        this._dead        = false;
        this._active      = true;
        this._attackTimer = 0;
        this._state       = 'CHASING';
        this._isJumping   = false;
        this._jumpVelY    = 0;

        // Random speed each activation: [1.75, 2.5]
        this.moveSpeed = 1.75 + Math.random() * 0.75;

        this.player.getPositionWorld(this._playerPos);
        vt.object.getPositionWorld(this._tmOffset);

        // Player grid pos = playerWorld - tmOffset
        const playerGX = this._playerPos[0] - this._tmOffset[0];
        const playerGZ = this._playerPos[2] - this._tmOffset[2];

        // Spawn band: inner 55% to full render radius so zombies arrive at staggered times
        const baseRadius = vt.renderDistance * vt.chunkSize;
        const radius = baseRadius * (0.55 + Math.random() * 0.45);

        // Cardinal flanking: distribute across 2–4 attack vectors that grow with night level
        const night = window.DeadVoxLoop?._nightLevel ?? 0;
        const numVectors = Math.min(2 + Math.floor(night / 3), 4);
        const vectorIdx  = Math.floor(Math.random() * numVectors);
        const baseAngle  = (vectorIdx / numVectors) * Math.PI * 2;
        const jitter     = (Math.random() - 0.5) * (Math.PI / 2.6); // ±35°
        const angle      = baseAngle + jitter;

        const spawnGX = playerGX + Math.cos(angle) * radius;
        const spawnGZ = playerGZ + Math.sin(angle) * radius;

        const surfaceGy = vt.getSurfaceHeight(spawnGX, spawnGZ);

        // setPositionLocal places zombie at grid coords directly
        this.object.setPositionLocal([spawnGX, surfaceGy + 1.0, spawnGZ]);
    }

    setRemoteState(x, y, z, hp, active) {
        this._isRemote = true;
        this._health   = hp;
        if (!active || hp <= 0) {
            this._dead   = true;
            this._active = false;
            this.object.setPositionLocal(this._hidePos ?? [0, -1000, 0]);
        } else {
            this._dead   = false;
            this._active = true;
            // Host sends getPositionLocal (grid coords); apply directly as local.
            this.object.setPositionLocal([x, y, z]);
        }
    }

    deactivate() {
        this._active = false;
        this._dead   = true;
        this.object.setPositionLocal(this._hidePos);
    }
}